import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthenticationService } from '../auth/authentication.service';
import {
  DiscoveryRun,
  ParcelIndexStatus,
  ProviderCredit,
  ParcelPage,
  DiscoverySettings,
  ScanStatus,
  SuspectedRentalAttachment,
  SuspectedRentalDetail,
  SuspectedRentalNote,
  SuspectedRentalOwner,
  SuspectedRentalPage,
  SuspectedRentalSource,
  SuspectedRentalStatus,
  TransitionRequest,
} from '../../models/discovery.model';

@Injectable({ providedIn: 'root' })
export class DiscoveryService {
  private readonly baseUrl = `${environment.apiUrl}/api/admin/discovery`;
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);

  private userHeaders(): HttpHeaders {
    const user = this.auth.getCurrentUser();
    let headers = new HttpHeaders();
    if (user?.id) headers = headers.set('X-User-Id', String(user.id));
    if (user?.username) headers = headers.set('X-User-Name', String(user.username));
    return headers;
  }

  search(filters: {
    status?: SuspectedRentalStatus;
    source?: SuspectedRentalSource;
    minConfidence?: number;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<SuspectedRentalPage> {
    let params = new HttpParams()
      .set('page', filters.page ?? 0)
      .set('size', filters.size ?? 25);
    if (filters.status) params = params.set('status', filters.status);
    if (filters.source) params = params.set('source', filters.source);
    if (filters.minConfidence != null) params = params.set('minConfidence', filters.minConfidence);
    if (filters.search) params = params.set('search', filters.search);
    return this.http.get<SuspectedRentalPage>(`${this.baseUrl}/suspects`, { params });
  }

  getDetail(id: number): Observable<SuspectedRentalDetail> {
    return this.http.get<SuspectedRentalDetail>(`${this.baseUrl}/suspects/${id}`);
  }

  transition(id: number, request: TransitionRequest): Observable<SuspectedRentalDetail> {
    return this.http.put<SuspectedRentalDetail>(`${this.baseUrl}/suspects/${id}/status`, request,
      { headers: this.userHeaders() });
  }

  /** Emails the registration notice to the owner email stored on the case. */
  sendNotice(id: number): Observable<SuspectedRentalDetail> {
    return this.http.post<SuspectedRentalDetail>(`${this.baseUrl}/suspects/${id}/notice`,
      {}, { headers: this.userHeaders() });
  }

  /** Registration letter PDF; the server records the case as postal-notified. */
  generateLetter(id: number): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/suspects/${id}/letter`, null,
      { headers: this.userHeaders(), responseType: 'blob' });
  }

  updateOwner(id: number, owner: SuspectedRentalOwner): Observable<SuspectedRentalDetail> {
    return this.http.put<SuspectedRentalDetail>(`${this.baseUrl}/suspects/${id}/owner`, owner,
      { headers: this.userHeaders() });
  }

  /** Create case from the task list: marks the case's Suspected Rental task acknowledged. */
  acknowledge(id: number): Observable<SuspectedRentalDetail> {
    return this.http.post<SuspectedRentalDetail>(`${this.baseUrl}/suspects/${id}/acknowledge`, null,
      { headers: this.userHeaders() });
  }

  addNote(id: number, content: string): Observable<SuspectedRentalNote> {
    return this.http.post<SuspectedRentalNote>(`${this.baseUrl}/suspects/${id}/notes`, { content },
      { headers: this.userHeaders() });
  }

  uploadAttachment(id: number, file: File): Observable<SuspectedRentalAttachment> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<SuspectedRentalAttachment>(`${this.baseUrl}/suspects/${id}/attachments`, form,
      { headers: this.userHeaders() });
  }

  startScan(sources: string[], limit?: number): Observable<ScanStatus> {
    return this.http.post<ScanStatus>(`${this.baseUrl}/runs`, { sources, limit: limit ?? null },
      { headers: this.userHeaders() });
  }

  scanStatus(): Observable<ScanStatus> {
    return this.http.get<ScanStatus>(`${this.baseUrl}/runs/status`);
  }

  runs(): Observable<DiscoveryRun[]> {
    return this.http.get<DiscoveryRun[]>(`${this.baseUrl}/runs`);
  }

  parcels(filters: { search?: string; page?: number; size?: number }): Observable<ParcelPage> {
    let params = new HttpParams()
      .set('page', filters.page ?? 0)
      .set('size', filters.size ?? 25);
    if (filters.search) params = params.set('search', filters.search);
    return this.http.get<ParcelPage>(`${this.baseUrl}/parcels`, { params });
  }

  parcelStatus(): Observable<ParcelIndexStatus> {
    return this.http.get<ParcelIndexStatus>(`${this.baseUrl}/parcels/status`);
  }

  providerCredit(): Observable<ProviderCredit> {
    return this.http.get<ProviderCredit>(`${this.baseUrl}/provider-credit`);
  }

  settings(): Observable<DiscoverySettings> {
    return this.http.get<DiscoverySettings>(`${this.baseUrl}/settings`);
  }

  saveSettings(settings: Partial<DiscoverySettings>): Observable<DiscoverySettings> {
    return this.http.put<DiscoverySettings>(`${this.baseUrl}/settings`, settings,
      { headers: this.userHeaders() });
  }

  downloadAttachment(id: number, attachmentId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/suspects/${id}/attachments/${attachmentId}/download`,
      { responseType: 'blob' });
  }
}
